import { useEffect, useRef, useState } from "react";
import { Search, MapPin, Loader2, X } from "lucide-react";
import { fetchAutocomplete } from "@/lib/api";

/**
 * Google Places Autocomplete picker.
 *
 * Props:
 *  - value: current input text
 *  - onChange(text): controlled text setter
 *  - onPick(suggestion): called when user selects a suggestion. The suggestion
 *    object includes `place_id`, `description`, `main_text`, `secondary_text`.
 *  - userLocation: { lat, lng } — biases suggestions toward this point
 *  - restaurantsOnly: true when picking a restaurant (Add a spot flow)
 *  - placeholder, testId, className
 *  - autoFocus
 *  - onSubmitText: optional fallback when user presses Enter without picking
 */
export default function PlaceAutocomplete({
  value,
  onChange,
  onPick,
  userLocation,
  restaurantsOnly = false,
  placeholder = "Search…",
  testId = "place-autocomplete",
  className = "",
  autoFocus = false,
  onSubmitText,
  variant = "default", // "default" | "rounded"
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef(null);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search
  useEffect(() => {
    if (!value || value.trim().length < 2) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        setLoading(true);
        const items = await fetchAutocomplete({
          input: value.trim(),
          lat: userLocation?.lat,
          lng: userLocation?.lng,
          restaurantsOnly,
          signal: abortRef.current.signal,
        });
        setSuggestions(items);
        setOpen(items.length > 0);
        setHighlight(-1);
      } catch (e) {
        if (e?.name !== "CanceledError" && e?.name !== "AbortError") {
          // silent — autocomplete is best-effort
        }
      } finally {
        setLoading(false);
      }
    }, 220);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [value, userLocation?.lat, userLocation?.lng, restaurantsOnly]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "Enter" && onSubmitText) {
        e.preventDefault();
        onSubmitText(value);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight >= 0 && suggestions[highlight]) {
        pick(suggestions[highlight]);
      } else if (onSubmitText) {
        onSubmitText(value);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const pick = (s) => {
    setOpen(false);
    setSuggestions([]);
    setHighlight(-1);
    onPick?.(s);
  };

  const inputCls =
    variant === "rounded"
      ? "w-full h-12 pl-11 pr-10 rounded-full bg-white border border-[#E5E1D8] shadow-sm text-sm placeholder:text-[#8A9A8B] focus:outline-none focus:ring-2 focus:ring-[#D95D39] focus:border-transparent transition-all"
      : "w-full h-10 pl-9 pr-8 rounded-xl border border-[#E5E1D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#D95D39]";

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <Search className={`absolute ${variant === "rounded" ? "left-4" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A9A8B]`} />
      <input
        data-testid={testId}
        type="text"
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        placeholder={placeholder}
        autoComplete="off"
        className={inputCls}
      />
      {loading ? (
        <Loader2 className={`absolute ${variant === "rounded" ? "right-4" : "right-2.5"} top-1/2 -translate-y-1/2 w-4 h-4 text-[#D95D39] animate-spin`} />
      ) : value ? (
        <button
          type="button"
          data-testid={`${testId}-clear`}
          onClick={() => {
            onChange("");
            setSuggestions([]);
            setOpen(false);
          }}
          className={`absolute ${variant === "rounded" ? "right-3" : "right-2"} top-1/2 -translate-y-1/2 w-6 h-6 inline-flex items-center justify-center rounded-full text-[#8A9A8B] hover:bg-[#F2EDE3]`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      ) : null}

      {open && suggestions.length > 0 && (
        <div
          data-testid={`${testId}-list`}
          className="absolute z-[60] left-0 right-0 mt-2 bg-white rounded-2xl border border-[#E5E1D8] shadow-xl overflow-hidden max-h-80 overflow-y-auto brand-scroll"
        >
          {suggestions.map((s, i) => (
            <button
              type="button"
              key={(s.place_id || s.description) + i}
              data-testid={`${testId}-item-${i}`}
              onClick={() => pick(s)}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full flex items-start gap-3 px-4 py-3 text-left text-sm transition-colors ${
                highlight === i ? "bg-[#FFF3EE]" : "bg-white hover:bg-[#FDFBF7]"
              } ${i > 0 ? "border-t border-[#F2EDE3]" : ""}`}
            >
              <MapPin className="w-4 h-4 mt-0.5 text-[#D95D39] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#2A3B2C] truncate">
                  {s.main_text || s.description}
                </div>
                {s.secondary_text && (
                  <div className="text-xs text-[#647466] truncate mt-0.5">
                    {s.secondary_text}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
